import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {isEnterKey, isSpaceKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export interface TabContainerHeaderDividerProps {
  className?: string;
}

export function TabContainerHeaderDivider(props: TabContainerHeaderDividerProps): JSX.Element {
  return <hr className={classNames('tabContainer-headerDivider', props.className)} />;
}

/** tabKeys may be user-facing labels; DOM ids must be valid CSS selectors for jsdom getComputedStyle. */
function tabDomId(tabKey: string, suffix: 'tab' | 'panel'): string {
  const safeKey = tabKey.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${safeKey}-${suffix}`;
}

/** @package */
export interface NavItemProps<T extends string> {
  active: boolean;
  centerNavItem?: boolean;
  className?: string;
  onClick: (tabKey: T) => void;
  panelId: string;
  tabKey: T;
  title: ReactNode;
  tabId: string;
}

/** @package */
export function NavItem<T extends string>(props: NavItemProps<T>): JSX.Element {
  function handleClick(): void {
    props.onClick(props.tabKey);
  }

  return (
    <div
      aria-controls={props.panelId}
      aria-selected={props.active}
      className={classNames(
        'tabContainer-navItem',
        {
          [css(styles.centeredNavItem)]: props.centerNavItem,
          'tabContainer-navItem--active': props.active,
        },
        props.className
      )}
      id={props.tabId}
      onClick={handleClick}
      onKeyDown={(e: ElementKeyboardEvent) => {
        if (isSpaceKey(e) || isEnterKey(e)) {
          handleClick();
        }
      }}
      role="tab"
      tabIndex={props.active ? -1 : 0}
      {...getTestProps({component: 'NavItem', key: `${props.tabKey}`})}
      data-amp-safe={false}
    >
      {props.title}
    </div>
  );
}

export interface TabContainerProps<T extends string> {
  activeTab: T;
  centerNavItem?: boolean;
  children: ReactNode;
  className?: string;
  isFullHeight?: boolean;
  headerDividerClassName?: string;
  onRequestTabChange: (tabKey: T) => void;
  navClassName?: string;
  paneClassName?: string;
  tabClassName?: string;
}

/*
 * TabContainer accepts tabKey on each Tab, and a defaultKey to specify which Tab to show first.
 * You can leave off tabKey and defaultKey to just show the first tab by default.
 */
export function TabContainer<T extends string>({
  activeTab,
  centerNavItem,
  children,
  className,
  isFullHeight = false,
  headerDividerClassName,
  onRequestTabChange,
  navClassName,
  paneClassName,
  tabClassName,
}: TabContainerProps<T>): JSX.Element {
  function isActiveTab(tab: React.ReactElement<TabProps<T>>): boolean {
    return tab.props.tabKey === activeTab;
  }

  function handleSwitchTab(tabKey: T): void {
    onRequestTabChange(tabKey);
  }

  // Filter out null/undefined children
  const tabArray = React.Children.toArray(children).filter((child) => child) as Array<
    React.ReactElement<TabProps<T>>
  >;
  const [activeTabElement] = tabArray.filter((tab) => isActiveTab(tab));
  return (
    <div className={className} {...getTestProps({component: 'TabContainer', element: 'root'})}>
      <div className={classNames('tabContainer-nav', navClassName)} role="tablist">
        {tabArray.map((child) => (
          <NavItem
            key={child.props.tabKey}
            active={isActiveTab(child)}
            centerNavItem={centerNavItem}
            className={tabClassName}
            onClick={handleSwitchTab}
            panelId={tabDomId(child.props.tabKey, 'panel')}
            tabId={tabDomId(child.props.tabKey, 'tab')}
            tabKey={child.props.tabKey}
            title={child.props.title}
          />
        ))}
      </div>
      <TabContainerHeaderDivider className={headerDividerClassName} />
      <div
        aria-labelledby={tabDomId(activeTabElement.props.tabKey, 'tab')}
        className={classNames('tabContainer-pane', paneClassName, {
          'u-flex-full-height': isFullHeight,
        })}
        id={tabDomId(activeTabElement.props.tabKey, 'panel')}
        role="tabpanel"
        {...getTestProps({component: 'TabContainer', element: 'pane'})}
      >
        {activeTabElement.props.children}
      </div>
    </div>
  );
}

export interface TabProps<T> {
  children: ReactNode;
  tabKey: T;
  title: ReactNode;
}

export function Tab<T>(_props: TabProps<T>): JSX.Element {
  throw new Error('Tab should not be actually rendered and is only used for external API');
}

const styles = StyleSheet.create({
  centeredNavItem: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    marginRight: 0,
  },
});
